import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import {
  AddThingToThingGroupCommand,
  AttachThingPrincipalCommand,
  AttachPolicyCommand,
  CreateKeysAndCertificateCommand,
  CreateThingCommand,
  DeleteThingCommand,
  DescribeCertificateCommand,
  DetachThingPrincipalCommand,
  GetPolicyCommand,
  IoTClient,
  ListThingGroupsCommand,
  ListThingGroupsForThingCommand,
  ListThingPrincipalsCommand,
  ListThingsCommand,
  ListThingsInThingGroupCommand,
  ListThingTypesCommand,
  RemoveThingFromThingGroupCommand,
} from '@aws-sdk/client-iot';
import {
  GetThingShadowCommand,
  IoTDataPlaneClient,
  UpdateThingShadowCommand,
} from '@aws-sdk/client-iot-data-plane';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import { Amplify, Auth } from 'aws-amplify';
import { BehaviorSubject, map, Subject, catchError, Observable } from 'rxjs';
import { Shadow } from '../constants/shadow';
import { LocalizationService } from './localization.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CognitoService } from './cognito.service';
import JSZip from 'jszip';
import { S3Service } from './s3.service';
import { SystemMessageService } from './system-message.service';
import { ModalService } from './device-modal.service';
import { RoleService } from './role.service';
import { BinsService, BinData } from './bins.service';
import { SleepTime } from '../constants/sleep-time';
import { ProductionStatus } from '../constants/device-poduction-status';
import { firstValueFrom } from 'rxjs';
import { assertValidAwsRegion } from './aws-region';

interface BinArrayItem {
  thing_name: string;
  bin_id: string;
}

export interface DeviceConfig {
  thing_name?: string;
  date?: number;
  dst?: number;
  env?: number;
  gps?: number;
  gtc?: number;
  his?: number;
  hit?: number;
  img?: number;
  lfr?: number;
  slp?: number;
  voc?: number;
  fwv?: string;
  ulib? : number;
}

export interface DeviceCarrierScan {
  mcc: string;
  cell_id: number;
  area: number;
  signal: number;
  mnc: string;
  serving_cell: string;
  arfcn: number;
}

export interface DeviceCarrier {
  fmv: string;
  icc: number;
  imei: number;
  oper: string;
  phn: string;
  scan?: DeviceCarrierScan;
  band?: string;
}

export interface DeviceDat {
  bat: number;
  dst: number;
  fdv: string;
  hib: number;
  hum: number;
  img: [];
  nct: number;
  pre: number;
  sig: number;
  tm0: number;
  tm2: number;
  tmp: number;
  vcc: number;
  voc: number;
  vb1?: number;
}

export interface ThingToCreate {
  thingName: string;
}

export interface Devices {
  thingName: string;
  bin?: BinData;
  thingARN: string;
  thingTypeName: string;
  gps: { latitude: number; longitude: number };
  location: string;
  lastUpdate: number;
  date: string;
  config?: DeviceConfig;
  previous_config?: DeviceConfig;
  car: DeviceCarrier;
  dat: DeviceDat;
  heartbeat?: {
    beat_miss?: number;
    supposed_to_communicate?: string | number;
    color?: string;
  };
  vb1?: number;
  prodStatus?: DeviceProductionStatus;
}

export interface DeviceProductionStatus {
  thing_name: string;
  status_index: number;
  status_label: string;
  reviewer: string;
  modified_reviewer: string;
  given_name: string;
  family_name: string;
}

export interface MultipleDevices {
  devices: Devices[];
}

export interface Device {
  thingName: string;
  thingTypeName: string;
  thingArn: string;
  attributes: any; // Set the correct type for attributes
  version: number;
}

export interface DeviceStatus {
  thingName: string;
  bin_id?: string;
  client_id?: string;
  distributor_id?: string;
  bind_model?: string;
  status?: string;
  last_update?: number;
  batery_level?: number;
  distance_to_lid?: number;
  bin_capacity?: any;
  fill_level?: any;
}

export interface ThingListForDistributorsAndClients {
  thingName: string;
  bin_id?: string;
  client_id?: string;
  client?: string;
  distributor_id?: string;
  distributor?: string;
  bind_model?: string;
  status?: string;
  last_update?: number;
  batery_level?: number;
  distance_to_lid?: number;
  bin_capacity?: any;
}

export interface Collection {
  thing_name: string;
  dst: number;
  dst_timestamp: number;
  preceding_dst: number;
  preceding_timestamp: number;
}

export interface CollectionCount {
  thing_name: string;
  collection_count: number;
  avg_preceding_dst: number;
}

export interface ThingStatusArray {
  thing_name: string;
  status: string;
  battery?: number;
  distance_to_lid?: number;
  last_update?: number;
  bin_capacity?: number;
  location?: string;
  fill_level?: number;
  bin_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IotService {
  public firmwareVersionArray: any[] = [];

  // Variables used for device association
  public selectedBinId: string = '';
  public selectedClientId: string = '';
  public selectedDistributorID: string = '';

  private iot!: IoTClient;
  private iotData!: IoTDataPlaneClient;

  // Variables for things
  public thingType: string = 'MWS';
  public thingGroupName: string = '';
  public thingShadow: any = {};
  public thingName: string = '';

  // Variables used for shadow update
  public shadowUpdated: boolean = false;

  public deviceAssociationRow: any;

  private date: string = '';

  public relationships: BinArrayItem[] = [];

  //variable used to show the delete thing modal
  public showDeleteThing: boolean = false;
  public showDeleteDataThing: boolean = false;
  public desiredDeleteThingName: string = '';
  public deleteReference: boolean = false;
  public deleteLogs: boolean = false;
  public deleteReportDatas: boolean = false;

  // Array of things and there data
  public thingStatusArray: ThingStatusArray[] = [];
  public thingsAllData: any;

  device: Devices = {
    thingName: '',
    thingARN: '', // Initialize other properties as needed
    thingTypeName: '',
    gps: { latitude: 0, longitude: 0 },
    location: '',
    lastUpdate: 0,
    date: '',
    config: {
      dst: 0,
      env: 0,
      gps: 0,
      gtc: 0,
      his: 0,
      hit: 0,
      img: 0,
      lfr: 0,
      slp: 0,
      voc: 0,
    },
    car: {
      fmv: '',
      icc: 0,
      imei: 0,
      oper: '',
      phn: '',
    },
    dat: {
      bat: 0,
      dst: 0,
      fdv: '',
      hib: 0,
      hum: 0,
      img: [],
      nct: 0,
      pre: 0,
      sig: 0,
      tm0: 0,
      tm2: 0,
      tmp: 0,
      vcc: 0,
      voc: 0,
    },
    prodStatus: {
      thing_name: '',
      status_index: 0,
      status_label: '',
      reviewer: '',
      modified_reviewer: '',
      given_name: '',
      family_name: '',
    },
  };

  public pourcentage: number = 0;
  public inCharge: boolean = false;
  public color: string = '';

  public ARRAY: any[] = [];

  // Variable to show/hide HTML components device-config-modal and device-graphs-modal
  public showDeviceConfigModal: boolean = false;
  public showDeviceTechnicalGraphs: boolean = false;

  public filteredArray: any[] = []; //Contains filtered results
  public devicesStatusArray: DeviceStatus[] = []; // Array of Device Status Objects
  public clientListThingsArray: DeviceStatus[] = [];
  public distributorListThingsArray: DeviceStatus[] = [];

  public listThingsArray: ThingListForDistributorsAndClients[] = []; // Array of List Things Objects
  public devicesArray: Devices[] = []; // Array of Device Objects
  public binArray: BinData[] = [];
  public thingArray: any = []; //Used for Iteration
  public biggestDeviceNumber: any; //Used to Determine the Min starting number of a new device
  lastDataFromAdminMap: number[] = []; //Coordinate Array for Zones

  // Variable used to display messages in lits when you create/update
  public successMessage: any;

  public cityLocation: string = '';

  // Variables needed for the filter
  public originalDevicesArray: Devices[] = [];
  public filterCount: number = 0;

  // Varirable that will be use to share things for device between components when you are in device-modal
  private selectedDevice = new BehaviorSubject<Devices | null>(null);
  selectedDevice$ = this.selectedDevice.asObservable();

  private shadowUpdate = new Subject<void>();
  shadowUpdate$ = this.shadowUpdate.asObservable();

  // Public property to store device bin ID
  public deviceBinId: any;

  // Public property to store bin capacity
  public binCapacity: any;

  public filteredThingListByClient: any;
  public filteredThingListByDistributor: any;

  public shadowsArray: any[] = [];

  public deviceNotes: any;

  // varaibles used in the device config logs
  public dst: number = 0;
  public env: number = 0;
  public voc: number = 0;
  public img: number = 0;
  public lfr: number = 0;
  public gps: number = 0;
  public gtc: number = 0;
  public slp: number = 0;
  public hit: number = 0;
  public his: number = 0;

  public ProductionStatus = ProductionStatus;
  public isCreateSubGroupModalOpen: boolean = false;

  constructor(
    private http: HttpClient,
    private s3: S3Service,
    private systemMessageService: SystemMessageService,
    public modal: ModalService,
    public cognitoService: CognitoService,
    private binService: BinsService
  ) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: environment.cognito.userPoolWebClientId,
          userPoolId: environment.cognito.userPoolId,
          identityPoolId: environment.cognito.identityPoolId,
          allowGuestAccess: true,
        },
      },
    });
    const credentials = this.getSessionCredentials();
    this.setIotClients(credentials);

    this.setConfig();
  }

  private getSessionCredentials(): AwsCredentialIdentity | undefined {
    const accessKeyId = sessionStorage.getItem('accessKeyId') || '';
    const secretAccessKey = sessionStorage.getItem('secretAccessKey') || '';
    const sessionToken = sessionStorage.getItem('sessionToken') || '';
    if (!accessKeyId || !secretAccessKey) {
      return undefined;
    }
    return { accessKeyId, secretAccessKey, sessionToken };
  }

  private setIotClients(credentials?: AwsCredentialIdentity): void {
    const region = assertValidAwsRegion(environment.iot.region);
    const endpoint = this.getIotDataEndpoint();
    this.iot = new IoTClient({ region, credentials });
    this.iotData = new IoTDataPlaneClient({
      region,
      ...(endpoint ? { endpoint } : {}),
      credentials,
    });
  }

  private getIotDataEndpoint(): string | undefined {
    const raw = environment.iot.endpoint?.trim();
    if (!raw) {
      return undefined;
    }
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    return `https://${raw}`;
  }

  // Function that configure user credentials
  async configure() {
    Amplify.configure({
      Auth: {
        Cognito: environment.cognito,
      },
    });

    //Returns temporary credentials for authenticated users
    const session = await this.cognitoService.ensureFreshCredentials(true);

    if (session && (session as any).identityId) {
      this.setIotClients({
        accessKeyId: session.accessKeyId,
        secretAccessKey: session.secretAccessKey,
        sessionToken: session.sessionToken,
      });

      await this.setConfig();
    }
  }

  // Function called to return the device array of all clients
  async getClientsThingList() {
    // Wait for the device list to be fetched before proceeding.
    await this.getDeviceList().then(() => {});

    // Return the array containing things for all clients.
    return this.clientListThingsArray;
  }

  // Function that return device of a distributor
  async getDistributorsThingList() {
    // Wait for the device list to be fetched before proceeding.
    await this.getDeviceList().then(() => {});

    // Return the array containing things for all clients.
    return this.distributorListThingsArray;
  }

  // Function that return the device list of a client
  async getThingListByClient(clientId: string) {
    try {
      // Wait for the device list to be fetched before proceeding.
      let devices;
      devices = await this.getDeviceList();
      this.filteredThingListByClient = devices.filter((thing) => {
        return thing.bin?.client_id === clientId;
      });
      return this.filteredThingListByClient;
    } catch (error) {
      console.warn('Error getting client things');
      console.error(error);
    }
  }

  // Function that return device list of a distributor
  async getThingListByDistributor(distributorId: string) {
    // Wait for the device list to be fetched before proceeding.
    await this.getDeviceList().then(() => {});

    // Filter the device list based on the provided distributor ID.
    this.filteredThingListByDistributor = this.devicesStatusArray.filter(
      (objeto) => objeto.client_id === distributorId
    );

    // Return the filtered array containing things for the specified distributor.
    return this.filteredThingListByDistributor;
  }

  //Attach Principal Policy to Authenticated User
  async setConfig() {
    try {
      const credentials = await Auth.currentCredentials();
      // Attach policy and principal policy
      if (credentials && credentials.identityId) {
        await this.iot.send(
          new GetPolicyCommand({ policyName: 'MWS-B01' })
        );
        await this.iot.send(
          new AttachPolicyCommand({
            policyName: 'MWS-B01',
            target: credentials.identityId,
          })
        );
      }
    } catch (error) {
      console.error('Error attaching policy:', error);
    }
  }

  //List all devices, excluding EGIOT devices
  async listThings() {
    await this.configure();
    try {
      const allThings = [];

      let nextToken: any = null;
      do {
        const params = { nextToken };

        //Call IOT Core to get Things
        const listThingsResponse = await this.iot.send(
          new ListThingsCommand(params)
        );
        const things = listThingsResponse.things || [];

        // Add the current page of things to the array
        allThings.push(...things);

        // Check if there are more things to retrieve
        nextToken = listThingsResponse.nextToken;
      } while (nextToken);

      //Check if things[] is populated
      if (allThings.length === 0) {
        return []; // Return an empty array if there are no devices
      }

      //Filter EGIOT devices from our results
      const filteredThings = allThings.filter((thing) => {
        // Check if thing is defined before accessing its properties
        return (
          thing &&
          thing.thingName &&
          !thing.thingName.startsWith('EGIOT') &&
          thing.thingTypeName?.startsWith('MWS')
        );
      });

      this.filteredArray.push(filteredThings);

      this.getLastDeviceNumber();
      return filteredThings;
      //return things;
    } catch (error) {
      this.cognitoService.checkForbidden(error);
      console.error('Error listing Things:', error);
      throw error;
    }
  }

  // Function that get last device number to increment it by 1
  getLastDeviceNumber() {
    this.biggestDeviceNumber = 0;

    let numbers = this.filteredArray[0].map(
      (item: {
        thingName: string;
        thingTypeName: string;
        thingArn: string;
        attributes: any;
        version: number;
      }) => {
        const thingName = item.thingName;
        // Use uma expressão regular para extrair a última numeração
        const matches = thingName.match(/(\d+)$/);
        // Verifique se houve correspondência e retorne o número como inteiro
        return matches ? parseInt(matches[0]) : 0;
      }
    );

    this.biggestDeviceNumber = Math.max(...numbers);
    this.biggestDeviceNumber++;
  }

  // Function used in device ilst to filter it by device group
  async filterThingGroup(group: string) {
    //await this.configure()
    try {
      const allThings = [];

      let thingGroupName: any = group;
      let nextToken: any = null;
      do {
        const params = { thingGroupName, nextToken };

        //Call IOT Core to get Things
        const listThingsResponse = await this.iot.send(
          new ListThingsInThingGroupCommand(params)
        );

        const things = listThingsResponse.things || [];

        // Add the current page of things to the array
        allThings.push(...things);

        // Check if there are more things to retrieve
        nextToken = listThingsResponse.nextToken;
      } while (nextToken);

      //Check if things[] is populated
      if (allThings.length === 0) {
        return []; // Return an empty array if there are no devices
      }

      //Filter EGIOT devices from our results
      const filteredThings = allThings.filter((thing) => {
        return thing;
      });

      this.filteredArray.push(filteredThings);

      this.getLastDeviceNumber();
      return filteredThings;
      //return things;
    } catch (error) {
      this.cognitoService.checkForbidden(error);
      console.error('Error listing Things:', error);
      throw error;
    }
  }

  //Get the Shadow for a specific device
  async getDeviceShadow(thingName: string) {
    try {
      // Specify the thingName of the device whose shadow you want to retrieve
      const params = { thingName }; // Use the device NAME here

      // Call the getThingShadow API to retrieve the device shadow
      const shadowResponse = await this.iotData.send(
        new GetThingShadowCommand(params)
      );

      // Check if the payload is present
      if (!shadowResponse.payload) {
        throw new Error('Payload is missing in the shadow response.');
      }

      // Parse and return the device shadow as a JSON object
      const payload =
        typeof shadowResponse.payload === 'string'
          ? shadowResponse.payload
          : new TextDecoder().decode(shadowResponse.payload as Uint8Array);
      const shadow = JSON.parse(payload);

      return shadow;
    } catch (error) {
      console.error(`Error retrieving shadow for device ${thingName}:`, error);

      this.cognitoService.verifySession();
    }
  }

  // Check health status using the provided thingName
  checkHealthStatusUsingThingName(thingName: any): string {
    // Find the device in the devicesStatusArray based on the thingName
    const device = this.devicesStatusArray.find(
      (device) => device.thingName === thingName
    );

    // Check if the device was found and if status is defined
    if (device && device.status !== undefined) {
      // Return the device status (assuming device.status is a string)
      return device.status;
    } else {
      // Handle the case where device is undefined or status is undefined
      // Return a default value, such as "Inactive"
      return 'Inactive';
    }
  }

  // Function that return fill_level of a bin with his device thing name
  checkDeviceFillLevelUsingThingName(thingName: any): string {
    const device = this.devicesStatusArray.find(
      (device) => device.thingName === thingName
    );

    if (device) {
      return device.fill_level;
    } else {
      return '';
    }
  }

  // Function that will get all device name in the AWS list
  async getDeviceList(): Promise<Devices[]> {
    this.devicesArray = [];
    this.filteredArray = [];
    this.originalDevicesArray = [];
    this.shadowsArray = [];

    // Get the array of all device name in the list and will loop through the array of attributes to made a new array of Devices
    await this.listThings()
      .then(async (list: any) => {
        this.thingArray = list;
        // this.checkStatusFunction();
        let things = [];
        for (let i = 0; i < this.thingArray.length; i++) {
          if (list[i].thingName !== undefined) {
            things.push(list[i].thingName);
          }
        }

        try {
          // Call the funciton to implement the device array
          await this.implementDeviceArray(things);
        } catch (error) {
          console.error(error);
          throw error;
        }
      })
      .catch((error) => {
        //Log errors for listThings()
        console.error('Error : ' + error);
        this.cognitoService.verifySession();
        return [];
      });

    // Put the devicesArray into an original array use to reset the filtered array whitout making a new call to AWS
    this.originalDevicesArray = [...this.devicesArray];
    //Return the final array
    return this.devicesArray;
  }

  // Funciton called to implement device array
  async implementDeviceArray(things: string[]) {
    const results: any = await firstValueFrom(
      await this.getThingShadows(things)
    );
    let i = 0;
    this.shadowsArray = JSON.parse(results.shadows);
    let shadows = JSON.parse(results.shadows);
    let bins = JSON.parse(results?.bins);
    let config = JSON.parse(results.configs);
    let production_status = JSON.parse(results.production_status);

    for (const shadow of shadows) {
      let vb1 = Number(parseFloat(shadow.vb1).toFixed(2));
      if (isNaN(vb1)) vb1 = 0;

      const thingDetails = this.getThingDetails(shadow.thing_name);
      const date = new Date(shadow.dst_timestamp * 1000);
      const formatedDate = this.formatDate(date);

      const device: Devices = {
        thingName: shadow.thing_name,
        bin: this.binService.filterBinData(bins, shadow.thing_name),
        thingARN: thingDetails?.thingARN ?? '',
        thingTypeName: thingDetails?.thingTypeName ?? '',
        gps: { latitude: 0, longitude: 0 },
        location: this.cityLocation,
        lastUpdate: shadow.dst_timestamp,
        date: formatedDate,
        config: this.filterDeviceConfig(config, shadow.thing_name, 'last'),
        previous_config: this.filterDeviceConfig(
          config,
          shadow.thing_name,
          'second_last'
        ),
        heartbeat: this.verifyDeviceHeartBeat(
          this.filterDeviceConfig(config, shadow.thing_name, 'last'),
          this.filterDeviceConfig(config, shadow.thing_name, 'second_last'),
          shadow.dst_timestamp,
          shadow.tm2
        ),
        car: {
          fmv: shadow.fmv ?? '',
          icc: shadow.icc ?? 0,
          imei: shadow.imei ?? 0,
          oper: shadow.oper ?? '',
          phn: shadow.phn ?? '',
        },
        dat: {
          bat: shadow.bat,
          dst: shadow.dst,
          fdv: shadow.fdv,
          hib: shadow.hib,
          hum: shadow.hum,
          img: shadow.img,
          nct: shadow.nct,
          pre: shadow.pre,
          sig: shadow.sig,
          tm0: shadow.tm0,
          tm2: shadow.tm2,
          tmp: shadow.tmp,
          vcc: shadow.vcc,
          voc: shadow.voc,
          vb1: Number(vb1) ?? 0,
        },
        prodStatus: this.filterProductionStatus(
          production_status,
          shadow.thing_name
        ),
      };

      this.devicesArray.push(device);
    }
  }

    // Funciton called to implement device array
  async adminDeviceArray() {
    const results: any = await firstValueFrom(
      await this.getAllThingShadows()
    );
    console.log(results)
    let i = 0;
    this.shadowsArray = JSON.parse(results.shadows);
    let shadows = JSON.parse(results.shadows);
    let bins = JSON.parse(results?.bins);
    let config = JSON.parse(results.configs);
    let production_status = JSON.parse(results.production_status);

    for (const shadow of shadows) {
      let vb1 = Number(parseFloat(shadow.vb1).toFixed(2));
      if (isNaN(vb1)) vb1 = 0;

      const thingDetails = this.getThingDetails(shadow.thing_name);
      const date = new Date(shadow.dst_timestamp * 1000);
      const formatedDate = this.formatDate(date);

      const device: Devices = {
        thingName: shadow.thing_name,
        bin: this.binService.filterBinData(bins, shadow.thing_name),
        thingARN: thingDetails?.thingARN ?? '',
        thingTypeName: thingDetails?.thingTypeName ?? '',
        gps: { latitude: 0, longitude: 0 },
        location: this.cityLocation,
        lastUpdate: shadow.dst_timestamp,
        date: formatedDate,
        config: this.filterDeviceConfig(config, shadow.thing_name, 'last'),
        previous_config: this.filterDeviceConfig(
          config,
          shadow.thing_name,
          'second_last'
        ),
        heartbeat: this.verifyDeviceHeartBeat(
          this.filterDeviceConfig(config, shadow.thing_name, 'last'),
          this.filterDeviceConfig(config, shadow.thing_name, 'second_last'),
          shadow.dst_timestamp,
          shadow.tm2
        ),
        car: {
          fmv: shadow.fmv ?? '',
          icc: shadow.icc ?? 0,
          imei: shadow.imei ?? 0,
          oper: shadow.oper ?? '',
          phn: shadow.phn ?? '',
        },
        dat: {
          bat: shadow.bat,
          dst: shadow.dst,
          fdv: shadow.fdv,
          hib: shadow.hib,
          hum: shadow.hum,
          img: shadow.img,
          nct: shadow.nct,
          pre: shadow.pre,
          sig: shadow.sig,
          tm0: shadow.tm0,
          tm2: shadow.tm2,
          tmp: shadow.tmp,
          vcc: shadow.vcc,
          voc: shadow.voc,
          vb1: Number(vb1) ?? 0,
        },
        prodStatus: this.filterProductionStatus(
          production_status,
          shadow.thing_name
        ),
      };

      this.devicesArray.push(device);
    }
  }

  // Function that return device details
  getThingDetails(
    searchThingName: string
  ): { thingARN: string; thingTypeName: string } | null {
    const foundThing = this.thingArray.find(
      (thing: any) => thing.thingName === searchThingName
    );
    if (foundThing) {
      return {
        thingARN: foundThing.thingARN,
        thingTypeName: foundThing.thingTypeName,
      };
    } else {
      return null; // Return null if no matching thingName is found
    }
  }

  // Functiion that set a date to a format date/time
  formatDate(date: Date) {
    // Retrieve the components of the local time
    const localDateString = date.toLocaleDateString();
    const localTimeString = date.toLocaleTimeString(undefined, {
      hour12: false,
    });

    // Concatenate the local date and time
    const formattedDateTime = `${localDateString} ${localTimeString}`;
    return formattedDateTime;
  }

  // Function that return shadow of things
  async getThingShadows(things: any = []) {
    // Append the 'user' parameter to the URL as a query string
    const url = environment.api.stage + environment.api.route.getThingShadows;

    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    return this.http.post(
      url,
      {
        things: things,
      },
      { headers: headers }
    );
  }

    // Function that return shadow of things
  async getAllThingShadows(things: any = []) {
    // Append the 'user' parameter to the URL as a query string
    const url = environment.api.stage + environment.api.route.getAllThingShadows;

    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    return this.http.post(
      url,
      {
        things: things,
      },
      { headers: headers }
    );
  }

  // Method to reorder the array by date in descending order
  reorderArrayByDateDescending(array: any) {
    array.sort((a: any, b: any) => {
      return b.lastUpdate - a.lastUpdate;
    });

    return array;
  }

  // Function to check the health status of a device
  // Parameters:
  // - lastUpdate: Timestamp of the last update in milliseconds
  // - distanceToLid: Distance from the lid in millimeters
  // - binCapacity: Total capacity of the bin in liters
  // - batteryVoltageRemaining: Remaining battery voltage in volts
  // Returns: Boolean indicating device health status (true if healthy, false if unhealthy)
  // to add fill level verification to health status check change to: checkDeviceHealthy(lastUpdate: number, distanceToLid: number, binCapacity: number, batteryVoltageRemaining: number) {
  checkDeviceHealthy(lastUpdate: number, batteryVoltageRemaining: number) {
    // Check the time since the last update
    let now = new Date().getDate();
    const hoursDifference = (now - lastUpdate) / (1000 * 60 * 60); // Calculate the difference in hours
    const hasExceeded48Hours: boolean = hoursDifference >= 48;

    // uncoment code below to activate fill level verification to health check
    // Check the fill level of the bin
    // const binCapacityInMM = binCapacity * 1000; // Convert capacity to millimeters
    // const percentageFill = ((binCapacity - distanceToLid) / binCapacity) * 100;
    // const percentageFillFixed = Math.max(0, Math.min(100, percentageFill));
    // const hasHighLevel: boolean = percentageFillFixed >= 80;

    // Check the battery level
    const percentageBattery = (batteryVoltageRemaining / 3.7) * 100;
    const hasLowBattery: boolean =
      percentageBattery < 30 || isNaN(percentageBattery);

    // Evaluate overall device health based on individual checks
    // to add fill level verification to health status check change to:if (hasLowBattery || hasExceeded24Hours || hasHighLevel) {
    if (hasLowBattery && hasExceeded48Hours) {
      return false;
    } else {
      // Device is healthy
      return true;
    }
  }

  /**
   * Retrieves a group of devices with identical coordinates based on input coordinates.
   *
   * @param dataFromAdminMap - An array containing latitude and longitude coordinates.
   * @param deviceList - A list of devices to filter based on coordinates.
   * @returns A Promise resolving to a list of devices with identical coordinates.
   */
  async getDeviceLocationGroup(
    dataFromAdminMap: number[],
    deviceList: Devices[]
  ): Promise<any> {
    // Validate input coordinates
    if (
      dataFromAdminMap.length !== 2 ||
      typeof dataFromAdminMap[0] !== 'number' ||
      typeof dataFromAdminMap[1] !== 'number'
    ) {
      // Return an empty list for invalid coordinates.
      return Promise.resolve([]);
    }

    // Check if input coordinates are the same as the last ones
    if (dataFromAdminMap !== this.lastDataFromAdminMap) {
      // Update the last known coordinates
      this.lastDataFromAdminMap = dataFromAdminMap;

      // Filter devices with identical coordinates
      const listOfDevicesWithIdenticalCoordinates = deviceList.filter(
        (device) => {
          if (
            device.gps &&
            device.gps.latitude === dataFromAdminMap[0] &&
            device.gps.longitude === dataFromAdminMap[1]
          ) {
            return true;
          }
          return false;
        }
      );

      // Return the list of devices with identical coordinates
      return Promise.resolve(listOfDevicesWithIdenticalCoordinates);
    }
  }

  // Reset the filtered array whitout making a new call to AWS
  resetDevicesArray() {
    this.devicesArray = [...this.originalDevicesArray];
  }

  // Will be set whit filter service whit the count of the string(userFilter) to know when user do backspace and reset the array
  setCountFilter(count: number) {
    this.filterCount = count;
  }

  // Function called to reset the device array
  resetDeviceArray() {
    this.devicesArray = [];
  }

  // Will be called by the component that call filter service to share the last userFilter count
  getCountFilter() {
    return this.filterCount;
  }

  // Function called by iot-list to display the modal
  async showConfig(thingName: string) {
    await this.configure();
    const shadow = await this.getDeviceShadow(thingName);
    if (!shadow) {
      this.showDeviceConfigModal = false;
      return;
    }

    // Set thingShadow with the device shadow data and show the modal
    this.thingShadow = shadow;
    this.thingName = thingName;
    this.showDeviceConfigModal = true;
  }
  async showTechnicalGraphs(thingName: string) {
    this.thingName = thingName; // Set service thingName to the thingName received
    this.showDeviceTechnicalGraphs = true;
  }

  //Create a singular Thing for IOT Core. AWS-sdk limits Creation by 1 Thing per call.
  async createThing(thingName: string): Promise<any> {
    // Step 1: Create the Thing
    const createThingParams = {
      thingName: thingName,
      thingTypeName: this.thingType, // User selected option
    };

    // Step 2: Associate the Thing with the Thing Group
    const thingGroupParams = {
      thingName: thingName,
      thingGroupName: this.thingGroupName,
    };

    //Create Thing using aws-sdk
    await this.iot.send(new CreateThingCommand(createThingParams));
    //Step 3: Adds Thing to Thing Group after creation
    await this.iot.send(new AddThingToThingGroupCommand(thingGroupParams));
    this.updateCertificate(thingName);
    // Step 4: Update the Thing Shadow
    return this.updateThingShadow(thingName);
  }

  //Called by @createThingAndUpdate
  async updateThingShadow(thingName: string, shadow: any = ''): Promise<any> {
    if (!thingName || thingName.trim().length === 0) {
      throw new Error('thingName must be a non-empty string');
    }
    if (shadow === '') {
      shadow = Shadow;
    }
    console.log('[DEBUG] Calling updateThingShadow with:', thingName);
    console.log('[DEBUG] new shadow', shadow);
    //Shadow is a global constant containing the Shadow structure including State and Metadata
    const updateThingShadowParams = {
      thingName: thingName,
      payload: new TextEncoder().encode(JSON.stringify(shadow)),
    };
    let res = await this.iotData.send(
      new UpdateThingShadowCommand(updateThingShadowParams)
    );
    console.log('[DEBUG] updateThingShadow', res);
    return res;
  }

  //Loops the provided (numberOfThings) number of times. Generates Thing Names and Creates a user-defined number of Things
  //Thing Name = EXAMPLE: MWS-BO1 (Thing Type + "-" + Thing Model)
  async createMultipleThings(
    numberOfThings: number,
    startingNumber: number,
    thingName: string,
    isCustomSufix: boolean = false,
    customSufix: number = 0
  ): Promise<any> {
    let currentThingNumber = 0;
    let currentThingName = '';
    let thingSuffix = '';
    for (let i = 0; i < numberOfThings; i++) {
      if (isCustomSufix) {
        // Generate the current thing name based on the starting number and iteration
        currentThingNumber = customSufix;
        thingSuffix = customSufix.toString();
      } else {
        // Generate the current thing name based on the starting number and iteration
        currentThingNumber = startingNumber + i;
        thingSuffix = currentThingNumber.toString();
      }
      if (currentThingNumber < 10) {
        thingSuffix = `00${currentThingNumber}`;
      } else if (currentThingNumber < 100) {
        thingSuffix = `0${currentThingNumber}`;
      }
      this.thingName = `${thingName}-${thingSuffix}`; //Concat a unique Thing Name
      // Set the current date format to yyyyMMDD
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      this.date = `${year}${month}${day}`;

      //Create and update the Thing for the current iteration
      await this.createThing(this.thingName)
        .then(() => {
          this.setDeviceAssociationLambda().subscribe((response) => {
            this.successMessage = response;
          });
          this.modal.thingName = this.thingName;
          this.modal.newNote = 'New device created'; // TODO add translation
          this.modal.saveNotes();

          // Call the function that will set new config in DB
          this.setNewDeviceConfig();
        })
        .catch((error) => {
          console.error(
            `Error creating or updating Thing ${currentThingName}:`,
            error
          );
          this.cognitoService.verifySession();
        });
    }
    return this.successMessage;
  }

  // Function that set default value for the new device config and made the call for the DB
  async setNewDeviceConfig() {
    this.dst = 1;
    this.env = 1;
    this.voc = 0;
    this.img = 0;
    this.lfr = 0;
    this.gps = 0;
    this.gtc = 1;
    this.slp = 3600;
    this.hit = -35;
    this.his = 28800;

    // Set user sub and date for the logs in DB
    const userSub = await this.cognitoService.getCurrentUserSub();
    const date = Math.floor(Date.now() / 1000);

    // Call the function that wirte in DB
    this.updateShadowDBLog(userSub, date);
  }

  // Function that will update client_thing table in DB if already exist or will create if not
  setDeviceAssociationLambda() {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Will call the lambda function in setThingClient url whit the passed data then return a response
    return this.http.post(
      environment.api.stage + environment.api.route.setDeviceAssociation,
      {
        thing_name: this.thingName,
        distributor_id: this.selectedDistributorID,
        client_id: this.selectedClientId,
        bin_id: this.selectedBinId,
        billable: 0,
        billable_timestamp: null,
        created: this.date,
        modified: this.date,
      },
      { headers: headers }
    );
  }

  // Function called to update the association of the device in deice association modal
  updateDeviceAssociationLambda(
    selectedClient: string,
    selectedBin: string,
    selectedDistributor: string,
    date: number,
    billable?: number,
    billableTimestamp?: number | null
  ) {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Call lambda fucntion whit the url of updateBin  and return the response
    const payload: any = {
      // Doubled coats things are used into lambda function as data and used for the SQL's calls that those functions does
      thing_name: this.thingName,
      distributor_id: selectedDistributor,
      client_id: selectedClient,
      bin_id: selectedBin,
      modified: date,
    };
    if (billable !== undefined) {
      payload.billable = billable;
    }
    if (billableTimestamp !== undefined) {
      payload.billable_timestamp = billableTimestamp;
    }
    return this.http.post(
      environment.api.stage + environment.api.route.updateDeviceAssociation,
      payload,
      { headers: headers }
    );
  }

  //Get all ThingTypes in IOT Core
  async getThingTypes(): Promise<any> {
    await this.configure();
    return this.iot.send(new ListThingTypesCommand({}));
  }

  //Get all ThingGroups in IOT Core
  async getThingGroups(): Promise<any> {
    await this.configure();
    return this.iot.send(new ListThingGroupsCommand({}));
  }

  //Update Group for Thing. Deletes from previous group prior to updating.
  async updateThingGroupForThing(
    thingName: string,
    newThingGroupName: string
  ): Promise<any> {
    await this.configure();
    // Step 1: Get current Thing Groups for the Thing
    const listThingGroupsParams = { thingName };

    const response = await this.iot.send(
      new ListThingGroupsForThingCommand(listThingGroupsParams)
    );
    const currentThingGroups = response.thingGroups || [];

    if (currentThingGroups.length > 0) {
      const removeThingParams = {
        thingGroupName: currentThingGroups[0].groupName,
        thingName,
      };
      await this.iot.send(new RemoveThingFromThingGroupCommand(removeThingParams));
    }

    const addThingParams = {
      thingGroupName: newThingGroupName,
      thingName,
    };
    return this.iot.send(new AddThingToThingGroupCommand(addThingParams));
  }

  //Delete Thing and remove from all groups
  async deleteThing(thingName: string): Promise<any> {
    await this.configure();

    const ListThingPrincipals = { thingName };

    try {
      const response = await this.iot.send(
        new ListThingPrincipalsCommand(ListThingPrincipals)
      );
      if (response.principals && response.principals.length > 1) {
        const principals = response.principals;

        for (const principal of principals) {
          const detachThingPrincipalRequest = {
            thingName,
            principal: principal.toString(),
          };

          await this.iot.send(
            new DetachThingPrincipalCommand(detachThingPrincipalRequest)
          );
        }
        //Step 1: Remove Thing from all Thing Groups
        await this.removeThingFromAllGroups(thingName);
        // Step 2: Delete the Thing
        const deleteThingParams = { thingName };
        return this.iot.send(new DeleteThingCommand(deleteThingParams));
      }
      if (response.principals && response.principals.length === 1) {
        const principal = response.principals;

        if (principal) {
          const detachThingPrincipalRequest = {
            thingName,
            principal: principal.toString(),
          };

          await this.iot.send(
            new DetachThingPrincipalCommand(detachThingPrincipalRequest)
          );
          //Step 1: Remove Thing from all Thing Groups
          await this.removeThingFromAllGroups(thingName);
          // Step 2: Delete the Thing
          const deleteThingParams = { thingName };
          return this.iot.send(new DeleteThingCommand(deleteThingParams));
        }
      }
      if (!response.principals) {
        //Step 1: Remove Thing from all Thing Groups
        await this.removeThingFromAllGroups(thingName);
        // Step 2: Delete the Thing
        const deleteThingParams = { thingName };
        return this.iot.send(new DeleteThingCommand(deleteThingParams));
      }
      return Promise.reject('Error in API');
      return response;
    } catch (error) {
      console.error('Error: ' + error);
      this.systemMessageService.selectRibbon(
        'danger',
        'alert-danger-generic-message'
      );
      throw error;
    }
  }

  //Remove Thing from all groups it might exist in
  async removeThingFromAllGroups(thingName: string): Promise<any> {
    await this.configure();
    // Get current Thing Groups for the Thing
    const listThingGroupsParams = { thingName };

    const response = await this.iot.send(
      new ListThingGroupsForThingCommand(listThingGroupsParams)
    );
    const currentThingGroups = response.thingGroups || [];

    const promises: Promise<any>[] = currentThingGroups.map((thingGroup: any) => {
      const removeThingParams = {
        thingGroupName: thingGroup.groupName,
        thingName,
      };
      return this.iot.send(
        new RemoveThingFromThingGroupCommand(removeThingParams)
      );
    });

    return Promise.all(promises);
  }

  // Function to add/update a certificate for a Thing in AWS IoT Core
  async updateCertificate(thingName: string): Promise<void> {
    await this.configure();
    try {
      // Generate a new key and certificate for the Thing
      const createKeysAndCertificateResponse = await this.iot.send(
        new CreateKeysAndCertificateCommand({ setAsActive: true })
      );

      const certificateArn =
        createKeysAndCertificateResponse.certificateArn || '';

      // Attach the certificate to the Thing
      await this.iot.send(
        new AttachThingPrincipalCommand({
          thingName,
          principal: certificateArn,
        })
      );

      // Attach the desired policy to the certificate
      await this.iot.send(
        new AttachPolicyCommand({
          policyName: 'MWS-B01', // Specify the policy name here
          target: certificateArn,
        })
      );

      // Retrieve the certificate details
      const certData = await this.iot.send(
        new DescribeCertificateCommand({
          certificateId: certificateArn.split('/')[1],
        })
      );

      // Create a zip folder using jszip
      const zip = new JSZip();

      // Add certificate files to the zip
      zip.file(
        'cert.crt',
        certData.certificateDescription?.certificatePem || ''
      ); //certificate
      // zip.file('public.pem.key', createKeysAndCertificateResponse.keyPair?.PublicKey || ''); // public key (not used)
      zip.file(
        'cert.key',
        createKeysAndCertificateResponse.keyPair?.PrivateKey || ''
      ); // private key for certificate

      // Generate the zip content
      const zipContent = await zip.generateAsync({ type: 'blob' });

      //Upload Certificates to s3
      this.s3.uploadCertificateFolderToS3(
        thingName,
        zipContent,
        `certificates-${thingName}.zip`
      );
    } catch (error) {
      console.error('Error adding/updating certificate:', error);
      this.cognitoService.verifySession();
      throw error;
    }
  }

  // Function called to delete a thing
  ShowDeleteThingModal(thingName: string) {
    this.thingName = thingName;

    // Set the HTML show/hide variables
    this.showDeleteThing = true;
  }

  // Method to get the count of devices
  /**
   * Asynchronously retrieves the count of devices based on the specified thing type and models.
   * @param thingType - The type of thing/device to filter.
   * @param models - An array of model names to filter devices.
   * @returns A Promise that resolves to an array of objects, each containing the model name and its corresponding device count.
   */
  async getDeviceCounts(
    thingType: string,
    models: string[]
  ): Promise<{ modelName: string; count: number }[]> {
    try {
      // Call listThings to get the list of devices
      const devices = await this.listThings();

      // Use map to create an array of objects with model names and their corresponding device counts
      const counts = models.map((model) => {
        const count = devices.reduce(
          (accumulator, device) =>
            device.thingName &&
            device.thingName.includes(this.buildSearchString(thingType, model))
              ? accumulator + 1
              : accumulator,
          0
        );

        return { modelName: model, count };
      });
      return counts;
    } catch (error) {
      this.cognitoService.verifySession();
      // Log and rethrow any errors that occur during the process
      console.error('Error getting device counts:', error);
      throw error;
    }
  }

  // Function that construct the search string in the format 'thingType-model'
  buildSearchString(thingType: string, model: string): string {
    return `${thingType}-${model}`;
  }

  //
  getThingCertificateDescription(thingName: string = 'MWS-B01-102'): void {
    this.iot
      .send(new ListThingPrincipalsCommand({ thingName }))
      .then((data: any) => {
        const certificateArn = data.principals?.[0];
        if (!certificateArn) return;

        const certificateId = certificateArn.split('/')[1];
        return this.iot.send(
          new DescribeCertificateCommand({ certificateId })
        );
      })
      .then((certData: any) => {
        if (!certData) return;
        const certJson = JSON.stringify(certData, null, 2);
        const blob = new Blob([certJson], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = 'thing_certificate.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch((err: any) => {
        console.error('Error fetching certificate details:', err);
      });
  }

  // Function triggered when shadow is updated in modal-device-config
  shadowSuccessUpdated() {
    this.shadowUpdate.next();
  }

  // Function called to insert a log in DB when a user update the shadow of a device
  updateShadowDBLog(userSub: string, date: number) {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Will call the lambda function in shadowUpdateLogs url whit the passed data then return a response
    return this.http
      .post(
        environment.api.stage + environment.api.route.shadowUpdateLogs,
        {
          // Doubled coats things are used into lambda function as data and used for the SQL's calls that those functions does
          thing_name: this.thingName,
          user_id: userSub,
          date: date,
          dst: this.thingShadow.state.desired.config.dst,
          env: this.thingShadow.state.desired.config.env,
          voc: this.thingShadow.state.desired.config.voc,
          img: this.thingShadow.state.desired.config.img,
          lfr: this.thingShadow.state.desired.config.lfr,
          gps: this.thingShadow.state.desired.config.gps,
          gtc: this.thingShadow.state.desired.config.gtc,
          slp: this.thingShadow.state.desired.config.slp,
          hit: this.thingShadow.state.desired.config.hit,
          his: this.thingShadow.state.desired.config.his,
        },
        { headers: headers }
      )
      .subscribe((response) => {});
  }

  // Funciton that return all notes of a device by API Gateway call
  async getDeviceNotes(thingName: string): Promise<any> {
    const url =
      environment.api.stage +
      environment.api.route.getDeviceNotes +
      '&thing_name=' +
      thingName;
    const response = await firstValueFrom(this.http.get(url));
    this.deviceNotes = response;
  }

  // function that return all device relationships
  getAllDevicerelationships() {
    // Append thingNmae to the url
    const url =
      environment.api.stage + environment.api.route.getDeviceRelationships;

    return this.http.get(url);
  }

  // Function that return a device relationship and all related to it, bin, client and distributor infos
  returnDeviceRelationship(thingName: string) {
    // Append thingNmae to the url
    const url =
      environment.api.stage +
      environment.api.route.returnDeviceRelationShipsByThingNameWithRelated +
      thingName;
    return this.http.get(url);
  }

  // Get all device associations
  async getDeviceRelationship(thingName: string) {
    // Append thingName to the url
    const url =
      environment.api.stage +
      environment.api.route.getDeviceRelationshipByThingname +
      thingName;

    return this.http.get(url).pipe(
      map(async (response) => {
        // Process the response data here if needed
        const tempDeviceAssociation = response;
        this.deviceAssociationRow = tempDeviceAssociation;

        this.deviceBinId = this.deviceAssociationRow.bin_id;

        // variables that receive parameters for creating the device to check health status
        let deviceLastUpdate;
        let deviceBateryLevel;
        let distanceToLid;
        let binCapacity;
        let status;
        let distributorId = this.deviceAssociationRow.distributor_id;
        let clientId = this.deviceAssociationRow.client_id;
        let fillLevel;

        // Search for the object in the devicesArray with the corresponding thingName
        const deviceFound = this.devicesArray.find(
          (device) => device.thingName === thingName
        );

        // Get the bin detail array
        this.modal.binArray = await this.binService.setBinDetailArray();

        if (deviceFound) {
          // if device found - update variables that will be used to create the device
          deviceLastUpdate = deviceFound.lastUpdate; // get timestamp
          deviceBateryLevel = deviceFound.dat.bat; // get battery remaining
          distanceToLid = deviceFound.dat.dst; // get distance to lid remaining

          binCapacity = deviceFound.bin?.bin_height; // get bin height (capacity)

          // get the fill level
          fillLevel = this.checkFillLevelPercentage(distanceToLid, binCapacity);

          // Checking device's health status
          if (binCapacity != null) {
            let isHealthy = this.checkDeviceHealthy(
              deviceLastUpdate,
              deviceBateryLevel
            );
            if (isHealthy) {
              status = 'Healthy';
            } else {
              status = 'Unhealthy';
            }
          } else {
            // set device health status to Unhealthy directly if bin capacity = null
            status = 'Inactive';
          }
        }

        // Create device status object
        const deviceStatus: DeviceStatus = {
          thingName: thingName, // Assign the thingName property
          bin_id: this.deviceBinId, // Assign the bin_id property
          distributor_id: distributorId, // Assign the distributor_id property
          client_id: clientId, // Assign the client_id property
          last_update: deviceLastUpdate, // Assign the last_update property
          batery_level: deviceBateryLevel, // Assign the battery_level property
          distance_to_lid: distanceToLid, // Assign the distance_to_lid property
          bin_capacity: binCapacity, // Assign the bin_capacity property
          status: status, // Assign the status property
          fill_level: fillLevel, // Assign the fill level property
        };

        // if the device has client id - create an array of things by client
        if (!!deviceStatus.client_id) {
          this.clientListThingsArray.push(deviceStatus);
        }

        // if the device has distributor id - create an array of things by client
        if (!!deviceStatus.distributor_id) {
          this.distributorListThingsArray.push(deviceStatus);
        }

        // Add device status object to devicesStatusArray
        this.devicesStatusArray.push(deviceStatus);
      }),
      catchError((error) => {
        // Log the API error for debugging purposes
        console.error('API Error:', error);
        // Re-throw the error for the calling code to handle
        throw error;
      })
    );
  }

  // Function that return battery pourcentage
  getBatteryPercentage(batteryRemaining: number) {
    // Will put this.pourcentage at zero if the pourcentage received is null or not a number
    if (
      (batteryRemaining !== 0 ||
        batteryRemaining !== null ||
        batteryRemaining !== undefined) &&
      typeof batteryRemaining === 'number'
    ) {
      // Convert the max voltage of the batery(3.7V) in pourcentage 0% to 100%
      this.pourcentage = Math.round((batteryRemaining / 3.7) * 100);
    } else {
      this.pourcentage = 0;
    }
    // If the batery is more then 3.7V it's because she is in charging mode
    if (batteryRemaining > 3.7) {
      this.pourcentage = 100;
      this.inCharge = true;
    }

    // Put the good color for the text depend on the pourcentage of the battery
    switch (true) {
      case this.pourcentage >= 0 && this.pourcentage <= 30:
        this.color = 'red';
        break;

      case this.pourcentage >= 31 && this.pourcentage <= 60:
        this.color = 'yellow';
        break;

      case this.pourcentage >= 61:
        this.color = 'green';
        break;

      default:
        this.color = 'red';
        break;
    }
    return this.pourcentage;
  }

  // Method to calculate and check the fill level percentage of a bin
  checkFillLevelPercentage(distanceToLid: number, binCapacity: any): string {
    // Flag to indicate if the verification can proceed
    let canBeverified = true;
    let result;

    // Calculate the percentage fill based on the distance to the lid and bin capacity
    const percentageFill = ((binCapacity - distanceToLid) / binCapacity) * 100;

    // Check if the battery level is invalid or empty
    // Check if the distance to the lid is greater than the bin capacity
    if (distanceToLid > Number(binCapacity)) {
      canBeverified = false;
      // uncomment to debug
      // console.error(
      //   "Problems when calculating the fill level - remaining fill level measured by the sensor is greater than the total capacity registered for the bin"
      //   );
    }

    // Check if the calculated percentage fill is NaN or if bin capacity is not provided
    if (Number.isNaN(percentageFill) || binCapacity == null) {
      canBeverified = false;
    }

    // If all checks pass, set the result to the calculated percentage fill, otherwise set to "N/A"
    if (canBeverified) {
      result = percentageFill.toFixed(2) + '%';
    } else {
      result = 'N/A';
    }

    return result;
  }

  // Function that return true if the gap between current date and dateToCompare is greater then 48hours
  isDateGapGreaterThan48Hours(dateToCompare: Date): boolean {
    // Get the current date and time
    const currentDate = new Date();

    // Calculate the time difference in milliseconds
    const timeDifference = currentDate.getTime() - dateToCompare.getTime();

    // Convert the time difference to hours
    const hoursDifference = timeDifference / (1000 * 60 * 60);

    // Check if the gap is greater than 48 hours
    return hoursDifference > 48;
  }

  // Function that return bin information if the device have a bin associated with
  isBinIdPopulated(thingName: string, array: BinArrayItem[]): boolean {
    const matchingItem = array.find((item) => item.thing_name === thingName);

    return !!matchingItem && !!matchingItem.bin_id;
  }

  // Function used in device modal delete thing when user confirm that he delete a thing
  confirmDeleteThing() {
    if (this.desiredDeleteThingName === this.thingName) {
      try {
        this.deleteThing(this.thingName).then((response) => {
          // const httpResponse = response.httpResponse;

          if (
            response.$response &&
            response.$response.httpResponse.statusCode === 200
          ) {
            this.showDeleteThing = false;
            this.showDeleteDataThing = true;
          }
        });
      } catch (error) {
        console.error('Error: ' + error);
        this.systemMessageService.selectRibbon(
          'danger',
          'alert-danger-generic-message'
        );
      }
    } else {
      this.systemMessageService.selectRibbon(
        'danger',
        'selectedThingNameNotGood'
      );
    }
  }

  // Function called in addition of delete thing to delete data of the thing in DB
  async confirmDeleteDataThing() {
    try {
      // Section when user select the sitch to delete device reference in DB
      if (this.deleteReference) {
        // Call the function that call the lambda function
        (await this.deleteReferenceLambda()).subscribe();
      }

      // Section when user select the switch to delete device logs from DB
      if (this.deleteLogs) {
        // Call the function that call the lambda function
        (await this.deleteLogsLambda()).subscribe();
      }

      // Section when user select the switch to delete report datas
      if (this.deleteReportDatas) {
        // Call the function that call the lambda function
        (await this.deleteReportDataLambda()).subscribe();
      }

      this.showDeleteDataThing = false;
      this.desiredDeleteThingName = '';
      this.thingName = '';
      window.location.reload();
    } catch (error) {
      console.error('Error: ' + error);
      this.systemMessageService.selectRibbon(
        'danger',
        'alert-danger-generic-message'
      );
    }
  }

  // Function that call the lambda function to delete all device reference
  async deleteReferenceLambda() {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Will call the lambda function in updateBinModel url whit the passed data then return a response
    return this.http.post(
      environment.api.stage + environment.api.route.deleteDeviceReferences,
      {
        // Doubled coats things are used into lambda function as data and used for the SQL's calls that those functions does
        thing_name: this.thingName,
      },
      { headers: headers }
    );
  }

  // Funciton that call the lambda function to delete all device logs
  async deleteLogsLambda() {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Will call the lambda function in updateBinModel url whit the passed data then return a response
    return this.http.post(
      environment.api.stage + environment.api.route.deleteDeviceLogs,
      {
        // Doubled coats things are used into lambda function as data and used for the SQL's calls that those functions does
        thing_name: this.thingName,
      },
      { headers: headers }
    );
  }

  // Function that call the lambda function to delete all device report datas
  async deleteReportDataLambda() {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    // Will call the lambda function in updateBinModel url whit the passed data then return a response
    return this.http.post(
      environment.api.stage + environment.api.route.deleteDeviceReportDatas,
      {
        // Doubled coats things are used into lambda function as data and used for the SQL's calls that those functions does
        thing_name: this.thingName,
      },
      { headers: headers }
    );
  }

  // Function that return shadow feed of things between two dates
  async getShadowFeed(
    things: string[],
    start: any,
    end: any,
    options?: { history?: boolean; historyLimit?: number }
  ): Promise<any[]> {
    // const url =
    //   environment.api.stage +
    //   environment.api.route.getHistoryByDate +
    //   '&things=' +
    //   things +
    //   '&start=' +
    //   start +
    //   '&end=' +
    //   end;
    // return this.http.get(url);
    const url = environment.api.stage + environment.api.route.getHistoryBatch;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const history: any[] = [];
    let cursor = start;
    let hasMore = true;

    while (hasMore) {
      const payload = {
        things,
        start,
        end,
        history: options?.history ?? true,
        history_limit: options?.historyLimit,
        history_cursor: cursor,
      };

      const response: any = await firstValueFrom(
        this.http.post(url, payload, { headers })
      );
      const batch = response?.history ?? [];
      if (batch.length > 0) {
        history.push(...batch);
      }

      const nextCursor = response?.history_meta?.next_cursor ?? null;
      hasMore = response?.history_meta?.has_more === true;
      if (!hasMore || nextCursor === null || nextCursor === cursor) {
        break;
      }

      cursor = nextCursor;
    }

    return history.map((row: any) => {
      const dstTimestamp = Number(row?.dst_timestamp);
      const hasDstTimestamp = Number.isFinite(dstTimestamp);
      return {
        ...row,
        dst_timestamp: hasDstTimestamp ? dstTimestamp : row?.dst_timestamp,
        timestamp:
          row?.timestamp ??
          (hasDstTimestamp ? dstTimestamp * 1000 : row?.timestamp),
      };
    });
  }

  // Function that filter the device config
  filterDeviceConfig(
    array: DeviceConfig[],
    thingName: string,
    position: string = 'last'
  ): DeviceConfig | undefined {
    // If there's multiple configuration that have been made on the device result will have the two last one
    const result = array.filter((item) => item.thing_name === thingName);

    // Return the lastest configuration for the device
    if (position === 'last') {
      return result[0];
    }
    // retunr the second lastest config for the device
    if (position === 'second_last' && result.length > 1) {
      return result[1];
    }
    return undefined;
  }

  /*
    In the function there is some + 300000, those are to let device a time line of 5 minutes in milisecond of gap to let him communicate with iot core
    before telling that the device doesn't work properly
    There's * 1000 too on config.slp and config.his because those are in second and we calculate in mili second
  */
  // Function that return device health depend on configs and time
  verifyDeviceHeartBeat(
    config: DeviceConfig | undefined,
    previousConfig: DeviceConfig | undefined,
    lastUpdate: number,
    lastTemperature: number
  ) {
    if (!(config && config.slp && config.his && config.hit)) return {};

    // Times (ms)
    const lastUpdateMs = Number(lastUpdate) * 1000;
    const nowMs = Date.now();
    const sleepBaseMs = Number(config.slp) * 1000; // base sleep
    const hiberBaseMs = Number(config.his) * 1000; // base hibernation
    const graceMs = 5 * 60_000; // 5 min grace

    // If config changed "recently" (within previous sleep period), fall back to previous-config logic
    const toMs = (v?: number | null) => {
      if (v == null) return 0;
      return v < 1e12 ? v * 1000 : v; // seconds -> ms if needed
    };

    const configDateMs = toMs(config?.date);

    const shouldUsePrevious =
      !!previousConfig && configDateMs > 0 && lastUpdateMs < configDateMs;

    if (shouldUsePrevious) {
      return this.checkPreviousConfigStatus(
        previousConfig,
        lastUpdate,
        lastTemperature
      );
    }

    // Miss counts using the new dynamic backoff:
    // (first miss after base; then +1h, +2h, +4h, +8h, +16h… capped at 16h)
    const sleepMisses = this.inferMisses(
      lastUpdateMs,
      sleepBaseMs,
      nowMs,
      graceMs
    );
    const hiberMisses = this.inferMisses(
      lastUpdateMs,
      hiberBaseMs,
      nowMs,
      graceMs
    );

    // Choose channel by temperature threshold (hibernate vs normal sleep)
    const useHibernate = Number(lastTemperature) < Number(config.hit);
    const misses = useHibernate ? hiberMisses : sleepMisses;
    const baseMs = useHibernate ? hiberBaseMs : sleepBaseMs;

    // Color policy: ≤1 miss = green, 2–3 = yellow, >3 = red
    const color = misses <= 1 ? 'green' : misses <= 3 ? 'yellow' : 'red';

    // Latest scheduled communication time given current misses:
    // t1 = lastUpdate + baseMs
    // t2 = t1 + 1h
    // t3 = t2 + 2h
    // ...
    const scheduledMs =
      lastUpdateMs + sleepBaseMs + this.cumulativeBackoffMs(misses);
    return {
      supposed_to_communicate: this.convertTimestamp(scheduledMs), // next expected from last ping
      beat_miss: misses,
      color: color,
    };
  }

  /** Sum of backoff increments for N misses: 1h, 2h, 4h, ... capped at 16h each step */
  cumulativeBackoffMs(misses: number): number {
    const HOUR = 60 * 60 * 1000;
    if (misses <= 0) return 0;
    let total = 0;
    for (let i = 0; i < misses; i++) {
      const step = Math.min(HOUR * (1 << i), 16 * HOUR);
      total += step;
    }
    return total;
  }

  /** Infer how many misses have elapsed since the last success, honoring the 16h cap per step. */
  /**
   * Infer how many consecutive misses have likely occurred since the last success,
   * given:
   *   - First threshold at baseMs (miss #1),
   *   - Then 1h, 2h, 4h, 8h, 16h (capped; each subsequent miss adds 16h).
   *
   * Examples for base=4h, last=12:00:
   *   16:05  -> 1 miss
   *   17:00  -> 2 misses
   *   20:00  -> 3 misses
   *   00:00  -> 4 misses
   *   08:00  -> 5 misses
   *   24:00+ -> 6+ (then +16h per extra miss)
   */
  inferMisses(
    lastUpdateMs: number,
    baseMs: number,
    nowMs: number = Date.now(),
    graceMs: number = 5 * 60000 // 5 minutes
  ): number {
    const HOUR = 60 * 60 * 1000;
    if (baseMs <= 0) return 0;

    // Time since last success minus grace
    const elapsedMs = nowMs - lastUpdateMs - graceMs;
    if (elapsedMs < baseMs) return 0; // not yet one miss
    const overMs = elapsedMs - baseMs;
    if (overMs <= 0) return 0;

    const overHours = overMs / HOUR;

    // Pre-cap: cumulative hours for m misses is S(m) = 2^m - 1 (m = 1..5) -> thresholds: 1,3,7,15,31
    if (overHours <= 31) {
      const m = Math.ceil(Math.log2(overHours + 1)); // 1..5
      return Math.min(Math.max(m, 1), 5);
    }

    // Post-cap: S(m) = 31 + (m-5)*16  (m >= 6)
    return 5 + Math.ceil((overHours - 31) / 16);
  }

  /*
    In the function there is some + 300000, those are to let device a time line of 5 minutes in milisecond of gap to let him communicate with iot core
    before telling that the device doesn't work properly
    There's * 1000 too on config.slp and config.his because those are in second and we calculate in mili second
  */
  // Function called by verifyDeviceHeartBeat in the case that the user change config by the time that the device communicate with his last configs
  /**
   * Called when config changed after the last successful communication and we must
   * evaluate heartbeat vs the *previous* config.
   */
  checkPreviousConfigStatus(
    previousConfig: DeviceConfig | undefined,
    lastUpdate: number, // seconds since epoch
    lastTemperature: number
  ) {
    if (
      !previousConfig ||
      !previousConfig.slp ||
      !previousConfig.his ||
      previousConfig.hit === undefined
    ) {
      return {};
    }

    const lastUpdateMs = Number(lastUpdate) * 1000;
    const sleepBaseMs = Number(previousConfig.slp) * 1000; // base sleep in ms
    const hiberBaseMs = Number(previousConfig.his) * 1000; // base hibernation in ms
    const nowMs = Date.now();
    const graceMs = 5 * 60_000; // 5 minutes

    // Infer misses for each channel using the same dynamic logic as verifyDeviceHeartBeat
    const sleepMisses = this.inferMisses(
      lastUpdateMs,
      sleepBaseMs,
      nowMs,
      graceMs
    );

    const hiberMisses = this.inferMisses(
      lastUpdateMs,
      hiberBaseMs,
      nowMs,
      graceMs
    );

    // Choose channel by temperature threshold (hibernate vs normal sleep)
    const useHibernate = Number(lastTemperature) < Number(previousConfig.hit);
    const misses = useHibernate ? hiberMisses : sleepMisses;
    const baseMs = useHibernate ? hiberBaseMs : sleepBaseMs;

    if (baseMs <= 0) return {};

    // Color policy: ≤1 miss = green, 2–3 = yellow, >3 = red
    const color = misses <= 1 ? 'green' : misses <= 3 ? 'yellow' : 'red';

    // Next scheduled attempt:
    // t1 = lastUpdate + baseMs
    // t2 = t1 + 1h
    // t3 = t2 + 2h
    // t4 = t3 + 4h ... each step capped at +16h
    const scheduledMs =
      lastUpdateMs + sleepBaseMs + this.cumulativeBackoffMs(misses);

    return {
      supposed_to_communicate: this.convertTimestamp(scheduledMs),
      beat_miss: misses,
      color,
    };
  }

  // Always returns local time string.
  // If `epoch` looks like seconds (< 1e12), we convert to ms.
  convertTimestamp(epoch: number | undefined, withSeconds = false): string {
    if (epoch == null) return '';

    const ms = epoch < 1e12 ? epoch * 1000 : epoch; // auto-detect sec vs ms
    const d = new Date(ms);

    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const HH = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());

    return withSeconds
      ? `${yyyy}/${MM}/${DD} ${HH}:${mm}:${ss}`
      : `${yyyy}/${MM}/${DD} ${HH}:${mm}`;
  }

  // Function called to convert second for sleep time in hour proper translatebla label
  convertSecondToHour(slp: number | undefined) {
    // Check if value receive is not undefined
    if (slp && slp !== undefined) {
      // Set the label of the sleep time constant configured
      const label = SleepTime.find((item) => item.value === Number(slp))?.label;
      return label ? label : '';
    }
    return '';
  }

  // Function called to validate battery voltage and return it with a "V" at the end
  validateVb1(vb1: number): number | string {
    if (vb1 == 0) {
      return '';
    }

    return vb1.toFixed(2) + 'V';
  }

  // Function called to check if heart beat is more then 100
  isBeatMissMoreThenOneHundred(beat_miss: number | undefined) {
    return beat_miss && beat_miss >= 100;
  }

  // Function used to filter the collection array with a thing name
  filterCollectionsByThingName(
    rows: Collection[],
    thingName: string
  ): Collection[] {
    return rows.filter((row) => row.thing_name === thingName);
  }

  // Function used to count number of collection by a thing name
  filterCollectionsCountsByThingName(
    rows: CollectionCount[],
    thingName: string
  ): CollectionCount | undefined {
    return rows.find((item) => item.thing_name === thingName);
  }

  filterProductionStatus(
    array: DeviceProductionStatus[],
    thingName: string
  ): DeviceProductionStatus {
    const value = array.find((item) => item.thing_name === thingName);

    if (value) {
      value.status_label = this.getStatusLabel(value.status_index);
      return value;
    } else {
      return {
        thing_name: thingName,
        status_index: 0,
        status_label: '',
        reviewer: '',
        modified_reviewer: '',
        given_name: '',
        family_name: '',
      };
    }
  }

  getStatusLabel(productionStatus: number): string {
    return (
      ProductionStatus.find((item) => parseInt(item.key) === productionStatus)
        ?.value || ''
    );
  }

  // Function that call API Gateway to initialize a device for production
  // It reset all datas in BD for a device
  initializeDeviceForProduction(thing_name: string) {
    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    return this.http.post(
      environment.api.stage +
        environment.api.route.initializeDeviceforProduction,
      {
        thing_name: thing_name,
      },
      { headers: headers }
    );
  }

  // Asynchronous function to count application devices and parameters, setting them as service properties
  async countDashboardDevicesAndParameters(thingsPassed: string[] = []) {
    // let data: any;
    if (thingsPassed.length === 0) {
      // Initializing arrays for healthy and unhealthy devices
      // Fetching the list of things
      const data = await this.listThings().then(async (result) => {
        let things = [];
        // Extracting thing names from the result
        for (const thing of result) {
          things.push(thing.thingName);
        }
        const data = await this.getDeviceHealth(things);
        return data;
      });
      return data;
    } else {
      const data = await this.getDeviceHealth(thingsPassed);
      return data;
    }
  }

  async getDeviceHealth(things: (string | undefined)[]) {
    const url = environment.api.stage + environment.api.route.getDeviceHealth;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const response = firstValueFrom(this.http.post(url, things, { headers }));
    const data = response.then(async (data: any) => {
      // console.log(data)
      data = JSON.parse(JSON.stringify(data));

      return {
        status: data.filteredArray,
        relationships: data.relationships,
        healthy: data.healthy,
        unhealthy: data.unhealthy,
        inactive: data.inactive,
        bins: data.bins,
        shadows: data.shadows,
        configs: data.configs,
      };
    });
    return data;
  }

  async addMissingConfigDefaultsToAllThings(): Promise<void> {
    await this.configure(); // Ensure AWS credentials are valid

    try {
      const allThings = await this.listThings(); // uses your paginated method

      for (const thing of allThings) {
        const thingName = thing.thingName;
        if (!thingName) continue;

        try {
          const shadow = await this.getDeviceShadow(thingName);

          const config = shadow?.state?.desired?.config;
          if (!config) {
            console.warn(
              `Device ${thingName} has no config object in desired state. Skipping.`
            );
            continue;
          }

          // Remove deprecated field
          config.upf = null
          config.furl = null;

          const updatePayload = {
            state: {
              desired: {
                config: config,
              },
            },
          };

          const params = {
            thingName,
            payload: new TextEncoder().encode(JSON.stringify(updatePayload)),
          };

          await this.iotData.send(new UpdateThingShadowCommand(params));
          console.log(`Updated config for ${thingName}`);
        } catch (shadowError) {
          console.error(`Failed to process device ${thingName}:`, shadowError);
          this.cognitoService.verifySession();
        }
      }
    } catch (listError) {
      console.error('Failed to retrieve device list:', listError);
      this.cognitoService.verifySession();
    }
  }

  // Function called to get all clients
  getFirmwareVersions() {
    // Append the 'user' parameter to the URL as a query string
    const url =
      environment.api.stage + environment.api.route.getFirmwareVersions;
    return this.http.get(url).pipe(
      map((response: any) => {
        // return the response data
        this.firmwareVersionArray = response;
        // For each element of the array, we'r gonna transfer the JSON received in integer for some of the keys

        return response;
      }),
      catchError((error) => {
        console.error('API Error:', error);
        throw error; // Re-throw the error for the calling code to handle
      })
    );
  }

  createDeviceSubGroup(
    subGroupName: string,
    client_id: string,
    thingList?: any[]
  ) {
    let url =
      environment.api.stage + environment.api.route.createDeviceSubGroup;

    const payload = {
      subGroupName: subGroupName,
      client_id: client_id,
      ...(thingList && thingList.length > 0 ? { thingList } : {}),
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    return this.http.post(url, payload, { headers: headers });
  }

  getClientSubGroups(client_id: string): Observable<any> {
    const url =
      environment.api.stage +
      environment.api.route.getClientSubGroup +
      '&client_id=' +
      client_id;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    return this.http.get(url, { headers: headers });
  }
  addDevicesToSubGroup(deviceList: any[], subGroupId: string) {
    const url =
      environment.api.stage + environment.api.route.addDevicesToSubGroup;
    const payload = {
      subGroupId,
      ...(deviceList && deviceList.length > 0 ? { deviceList } : {}),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    return this.http.post(url, payload, { headers: headers });
  }
  removeDevicesFromSubGroup(
    deviceList: any[],
    subGroupId: string
  ): Observable<any> {
    const url =
      environment.api.stage + environment.api.route.removeDevicesFromSubGroup;
    const payload = {
      subGroupId,
      ...(deviceList && deviceList.length > 0 ? { deviceList } : {}),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    return this.http.post(url, payload, { headers: headers });
  }
  deleteSubGroup(
    sub_groupID: string,
    transferMap?: { thingName: string; targetGroup: string }[]
  ) {
    const url = environment.api.stage + environment.api.route.deleteSubGroup;
    const payload: any = { sub_groupID };

    if (transferMap && transferMap.length > 0) {
      payload.transferMap = transferMap;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(url, payload, { headers: headers });
  }
  async changeThingGroup(
    thingName: string,
    oldGroupName: string,
    newGroupName: string
  ): Promise<void> {
    //Remove thing from the old group
    const removeParams = {
      thingName: thingName,
      thingGroupName: oldGroupName,
    };
    const result = await this.iot.send(
      new ListThingGroupsForThingCommand({ thingName })
    );
    console.log('thing group', result.thingGroups);
    // return result.thingGroups;
    if (result.thingGroups && result.thingGroups?.length > 0) {
      try {
        await this.iot.send(new RemoveThingFromThingGroupCommand(removeParams));
      } catch (err) {
        console.error(`Failed to remove thing from group: ${err}`);
        throw err;
      }
    }

    //Add  thing to  new group
    const addParams = {
      thingName: thingName,
      thingGroupName: newGroupName,
    };

    try {
      await this.iot.send(new AddThingToThingGroupCommand(addParams));
    } catch (err) {
      console.error(`Failed to add thing to new group: ${err}`);
      throw err;
    }
  }
  async getThingGroupsForThing(thingName: string): Promise<string[]> {
    const params = { thingName };

    try {
      const result = await this.iot.send(
        new ListThingGroupsForThingCommand(params)
      );

      const groupNames = (result.thingGroups || [])
        .map((group: any) => group?.groupName)
        .filter((name: any): name is string => typeof name === 'string');

      return groupNames;
    } catch (err) {
      console.error(`Error fetching groups for thing "${thingName}":`, err);
      throw err;
    }
  }
}
